import { useState } from "react";
import { Button, Combobox, FormField, Modal, TextInput } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import { DrawerWarning } from "./DrawerWarning";
import { useMoney } from "@/shared/ui";
import { overpaidOnBill } from "@/modules/pos/lib/partyShares";
import {
  VOID_REASON_NOTE_MAX_LENGTH,
  canConfirmVoid,
  isVoidReasonNoteRequired,
  nextReasonCodeForQuery,
  reasonCodesForKind,
  voidReasonLabel,
  type ReasonCode,
} from "@/modules/pos/lib/voidReasons";
import type { Invoice } from "@/lib/api/types";
import {
  UnsavedChangesPrompt,
  useDiscardGuard,
} from "@/shared/UnsavedChangesPrompt";

/**
 * GIVING BACK MONEY THE BILL NO LONGER ASKS FOR (B89).
 *
 * The state this closes: a family pays 300 of a 500 bill, a plate they never
 * got is voided, the bill re-closes at 100. The restaurant is holding 200
 * nobody owes it — and before this there was no act that finished such a bill.
 * The outstanding balance floored at zero, which READS as square, a tender of
 * zero is refused, and the only remaining move on the floor was to open the
 * drawer and record nothing.
 *
 * **NO AMOUNT FIELD, and there never will be.** The figure is `tendered −
 * total`, both of them the server's own; this is the only command in the
 * product that pays out without a plate to price it from, so a till that could
 * type it could type the wrong number. What the dialog does instead is SHOW the
 * arithmetic — the bill, what was paid, the difference — so the cashier can
 * check it against the drawer before confirming rather than after.
 *
 * **A REASON IS REQUIRED**, on the same closed vocabulary a plate refund uses
 * and not a fourth one: handing back an over-payment is a refund with a
 * different figure behind it, and a cashier picking a reason should meet words
 * they already know. The shape CHECK on the movement kind demands one, because
 * a row that hands money back without saying why is the one an auditor cannot
 * tell from theft.
 *
 * **IT SETTLES THE BILL.** The payout brings what has been tendered down to the
 * total, and settled-ness is derived from that — so the sentence says so, since
 * "the bill closes" is the half the cashier is actually trying to achieve.
 */
export function OverpaymentDialog({
  invoice,
  isSubmitting,
  warning,
  onOpenDrawer,
  onCancel,
  onConfirm,
}: {
  invoice: Invoice;
  isSubmitting: boolean;
  /**
   * The D25 pre-warning, on the same terms as the refund sheet's: money is
   * PHYSICALLY leaving a till, so with tracking on and no drawer open the
   * server refuses this as `RESTAURANT_SHIFT_REQUIRED`.
   */
  warning?: string;
  /**
   * FIX IT WITHOUT LEAVING THIS SHEET (Unit C) — opens the drawer dialog over it.
   *
   * This sheet used to state the refusal and stop there, which left a cashier
   * holding a customer's money and no move that keeps the sale on the system.
   * Omitted for anyone without the drawer rung: their sentence names a person to
   * ask, and a button whose only outcome is a refusal teaches them to stop reading
   * the warning.
   */
  onOpenDrawer?: () => void;
  onCancel: () => void;
  onConfirm: (reasonCode: string, reasonNote: string) => void | Promise<void>;
}) {
  const t = useTranslate();
  const money = useMoney();

  // DEF-7: nothing pre-selected. A pre-filled picker reads as already answered
  // and the stale value goes out on the command log — on a money-out row that
  // is the reason an auditor reads a year later.
  const [reasonCode, setReasonCode] = useState<ReasonCode | null>(null);
  const [reasonNote, setReasonNote] = useState("");

  const amount = overpaidOnBill(invoice);
  const noteRequired = isVoidReasonNoteRequired(reasonCode);
  /* `canConfirmVoid`, the TWO-argument rule the refund sheet uses, and not
     `canConfirmVoidReason`, which only asks whether a code was picked. This
     shipped calling the one-argument helper with two arguments — so OTHER,
     the one code that says nothing about what happened, enabled a money-out
     confirm with no note beside it and the refusal came from the server after
     the tap. `tsc` had been reporting it. */
  const canConfirm =
    amount > 0 && canConfirmVoid(reasonCode, reasonNote) && !isSubmitting;

  // The reason and its note are the whole of the typed work here — the figure
  // is derived. Both are the row an auditor reads a year later.
  const guard = useDiscardGuard(
    reasonCode !== null || reasonNote.trim() !== "",
    onCancel,
  );

  return (
    <>
    <Modal
      isOpen
      onClose={guard.requestClose}
      size="sm"
      title={t((m) => m.pos.overpaidTitle)}
      closeLabel={t((m) => m.common.close)}
      data-testid="overpayment-dialog"
      footerActions={
        <>
          <Button
            variant="outline"
            size="touch"
            disabled={isSubmitting}
            onClick={guard.requestClose}
            data-testid="overpayment-cancel"
          >
            {t((m) => m.common.cancel)}
          </Button>
          {/* Solid danger, like the refund sheet's confirm: this IS money
              leaving the drawer, and the button says how much so the last thing
              read before the tap is the figure. */}
          <Button
            variant="danger"
            size="touch"
            className="font-extrabold"
            disabled={!canConfirm}
            isLoading={isSubmitting}
            data-testid="overpayment-confirm"
            onClick={() => {
              if (!canConfirm || reasonCode === null) return;
              void onConfirm(reasonCode, reasonNote.trim());
            }}
          >
            {t((m) => m.pos.overpaidConfirm, { amount: money(amount) })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/* THE ARITHMETIC, not just the answer. A cashier about to take money
            out of a drawer has to be able to check the figure against the bill
            in front of them, and "give back 172.32" alone is not checkable. */}
        <div className="flex flex-col gap-0.5 tabular-nums">
          <div className="flex justify-between text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]">
            <span>{t((m) => m.pos.running)}</span>
            <span>{money(invoice.total)}</span>
          </div>
          <div className="flex justify-between text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]">
            <span>{t((m) => m.pos.partialPaidSoFar)}</span>
            <span>{money(invoice.tenderedTotal)}</span>
          </div>
          <div className="mt-[3px] flex items-baseline justify-between border-t border-dashed border-[color:var(--border-strong)] pt-1.5">
            <span className="text-[length:calc(var(--font-size-xs)_+_0.5px)] font-extrabold">
              {t((m) => m.pos.overpaidLabel)}
            </span>
            <span
              className="text-[length:var(--font-size-xl)] font-extrabold tabular-nums text-[color:var(--danger)]"
              data-testid="overpayment-amount"
            >
              {money(amount)}
            </span>
          </div>
        </div>

        <p className="text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold leading-relaxed text-[color:var(--text-secondary)]">
          {t((m) => m.pos.overpaidBody, {
            total: money(invoice.total),
            paid: money(invoice.tenderedTotal),
          })}
        </p>

        {warning ? (
          <DrawerWarning
            message={warning}
            actionLabel={t((m) => m.pos.noShiftWarningOpen)}
            onOpenDrawer={onOpenDrawer}
            testId="overpayment-shift-warning"
          />
        ) : null}

        <FormField name="overpaidReason" label={t((m) => m.pos.refundReason)}>
          <Combobox
            value={reasonCode ?? ""}
            options={reasonCodesForKind("refund").map((code) => ({
              value: code,
              label: voidReasonLabel(t, code),
            }))}
            onQueryChange={(query) =>
              setReasonCode((current) =>
                nextReasonCodeForQuery(t, current, query),
              )
            }
            onChange={(value) => setReasonCode((value as ReasonCode) || null)}
            data-testid="overpayment-reason"
          />
        </FormField>

        <FormField
          name="overpaidReasonNote"
          // The requirement in the label, so a cashier who picks OTHER sees WHY
          // the button is dead rather than guessing at it.
          label={
            noteRequired
              ? `${t((m) => m.pos.voidReasonNoteLabel)} (${t((m) => m.common.required)})`
              : t((m) => m.pos.voidReasonNoteLabel)
          }
        >
          <TextInput
            name="overpaidReasonNote"
            value={reasonNote}
            maxLength={VOID_REASON_NOTE_MAX_LENGTH}
            dir="auto"
            onChange={(event) => setReasonNote(event.target.value)}
            data-testid="overpayment-reason-note"
          />
        </FormField>

      </div>
    </Modal>
    <UnsavedChangesPrompt
      isOpen={guard.asking}
      onDiscard={guard.discard}
      onKeepEditing={guard.keepEditing}
      testId="overpayment-discard-prompt"
    />
    </>
  );
}
